import { matchPath } from 'react-router-dom';

const ALLOWED_FILTERS = new Set([
  'search', 'clinics', 'limit', 'logType', 'mealType', 'nutrition', 'glucose',
]);
const ALLOWED_TABS = new Set([
  'summary', 'logs', 'logs-v2', 'nutrients', 'prescriptions', 'recipes', 'meal-plans',
  'emotions', 'reports', 'resources', 'subscriptions',
]);
const ALLOWED_VARIANTS = new Set([
  'recipes', 'meal-plan', 'food-substitutions',
]);
const ALLOWED_GOAL_TYPES = new Set(['nutrient', 'frequency']);
const ALLOWED_REPORT_SECTIONS = new Set([
  'Clinician Generated', 'RxFood Generated', 'Sent',
]);

/** Send only known, bounded analytics properties to the browser SDK. */
export function sanitizeSegmentation(
  properties: Record<string, unknown>,
): Record<string, string | number | boolean> {
  const result: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (key === 'filter' && typeof value === 'string' && ALLOWED_FILTERS.has(value)) {
      result[key] = value;
    } else if (key === 'tab' && typeof value === 'string' && ALLOWED_TABS.has(value)) {
      result[key] = value;
    } else if (key === 'variant' && typeof value === 'string' && ALLOWED_VARIANTS.has(value)) {
      result[key] = value;
    } else if (key === 'Report Section' && typeof value === 'string' && ALLOWED_REPORT_SECTIONS.has(value)) {
      result[key] = value;
    } else if (key === 'tip_length' && typeof value === 'number' && Number.isFinite(value)) {
      result[key] = Math.max(0, Math.min(300, Math.floor(value)));
    } else if (key === 'goal_type' && typeof value === 'string' && ALLOWED_GOAL_TYPES.has(value)) {
      result[key] = value;
    }
  }
  return result;
}

const UNKNOWN_PATH = '/other';

const PATIENT_VIEWS = [
  'summary', 'log', 'logv2', 'nutrients', 'prescriptions', 'recipes', 'emotions', 'dash', 'reports',
  'resources', 'subscriptions',
];

/**
 * Every route the portal serves, in the form the gateway accepts. Keep in sync with the
 * routers in App.tsx, patient.tsx and patient-meal-plans.tsx. Only these strings are ever emitted.
 */
export const ANALYTICS_ROUTE_TEMPLATES: readonly string[] = [
  '/',
  '/login',
  '/logout',
  '/signup',
  '/forgotpassword',
  '/resources',
  '/patients',
  '/patients/reports/cgm/:id',
  '/patient',
  '/patient/:id',
  ...PATIENT_VIEWS.map(view => `/patient/:id/${view}`),
  '/patient/:id/meal-plan',
  '/patient/:id/meal-plan/new',
  '/patient/:id/meal-plan/:id',
];

function normalizePath(pathname: string): string | null {
  const withoutQueryOrHash = pathname.split(/[?#]/)[0];
  if (/%2f/i.test(withoutQueryOrHash)) {
    return null;
  }
  let decoded: string;
  try {
    decoded = decodeURIComponent(withoutQueryOrHash);
  } catch {
    return null;
  }
  const path = decoded.toLowerCase().replace(/\/{2,}/g, '/').replace(/(.)\/+$/, '$1');
  return path.startsWith('/') ? path : `/${path}`;
}

/** Map a location to a known route template. Anything else reports as `/other`. */
export function parameterizePath(pathname: string): string {
  const path = normalizePath(pathname);
  if (path === null) {
    return UNKNOWN_PATH;
  }
  const template = ANALYTICS_ROUTE_TEMPLATES.find(
    candidate => matchPath(path, { path: candidate, exact: true, strict: true }),
  );
  return template ?? UNKNOWN_PATH;
}
